import { useState, useCallback } from 'react';
import { FileTypeMap, getFileExtension } from '../../utils/fileUtils';
import { useUser } from '../../features/authentication';
import uuid from '../../services/uuid';
import { handleError } from '../../utils/apiUtils';
import uploadService from '../../services/uploadService';
import { AxiosError } from 'axios';
import { FileUploaderProps, UploadedFile } from './models';

const ErrorReason = Object.freeze({
  EXCEEDS_MAXSIZE: 'exceeds_maxsize',
  INVALID_TYPE: 'invalid_type',
  EMPTY_FILE: 'empty_file',
});

const buildAcceptsData = (typeMap: typeof FileTypeMap, accepts?: (keyof typeof FileTypeMap)[]) => ({
  mimeTypes: accepts ? accepts.map(type => typeMap[type]).join(',') : Object.values(FileTypeMap).join(','),
  message: `We accept ${
    accepts
      ? accepts.reduce(
          (msg, type, i) =>
            msg
              .concat(accepts.length > 1 && i === accepts.length - 1 ? ' and ' : '')
              .concat(type)
              .concat(i < accepts.length - 2 ? ', ' : ''),
          ''
        )
      : 'image, audio, video, document, epub and zip'
  } files`,
});

const getErrorMessage = (reason: string | null): string | null => {
  if (!reason) return null;

  const messages: Record<string, string> = {
    [ErrorReason.EXCEEDS_MAXSIZE]: 'File is too large - 8 GB max size',
    [ErrorReason.INVALID_TYPE]: 'File type is invalid',
    [ErrorReason.EMPTY_FILE]: 'Invalid file - file is empty',
  };
  return messages[reason] || null;
};

export const useFileUploader = ({
  orgId,
  initialFile,
  onFileUploadStarted,
  onFileUploaded,
  onFileRemoved,
  uploadLocation,
  accepts,
}: FileUploaderProps) => {
  const [file, setFile] = useState(initialFile);
  const [errorReason, setErrorReason] = useState('');
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const acceptsData = buildAcceptsData(FileTypeMap, accepts);
  const maxSize = 8589934592; //8gb- increasing this for videos, we may want to consider different maxSizes per media type in the future
  const { user } = useUser();
  const userId = user?.userId ?? '';

  const updateFile = (file?: UploadedFile) => {
    setFile(file || null);
    if (file) {
      onFileUploaded(file);
    }
  };

  const removeFile = () => {
    setFile(null);
    setUploadProgress(null);
    onFileRemoved();
  };

  const cancelUpload = () => {
    uploadService.cancel('Operation canceled by the user.');
    removeFile();
  };

  const getUploadLocation = (location: string | ((file: File) => string), file: File): string => {
    if (typeof location === 'string') return location;
    if (typeof location === 'function') return location(file);
    return '';
  };

  const onProgress = ({ loaded, total }: { loaded: number; total: number }) => {
    setUploadProgress(Math.floor((loaded / total) * 100));
  };

  const normalizeFileName = (fileName: string) => fileName.replace(/ /g, '');

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: File[]) => {
      if (rejectedFiles.length) {
        console.warn('Rejected File: ', rejectedFiles[0]);
        updateFile();
        setErrorReason(rejectedFiles[0].size > maxSize ? ErrorReason.EXCEEDS_MAXSIZE : ErrorReason.INVALID_TYPE);
      } else {
        const acceptedFile = new File([acceptedFiles[0]], normalizeFileName(acceptedFiles[0].name), {
          type: acceptedFiles[0].type,
        });

        if (acceptedFile.size === 0) {
          updateFile();
          setErrorReason(ErrorReason.EMPTY_FILE);
          return;
        }

        setErrorReason('');
        if (onFileUploadStarted) onFileUploadStarted();

        const id = uuid.generate();
        const extension = getFileExtension(acceptedFile.name);

        const location = getUploadLocation(uploadLocation, acceptedFile);

        const promise = location
          ? uploadService.upload(location, `${id}.${extension}`, acceptedFile, onProgress, true)
          : uploadService.uploadFile(id, orgId, acceptedFile.name, acceptedFile, true, userId, onProgress);

        promise
          .then(() => {
            const uploadedFile = { id, name: acceptedFile.name, type: acceptedFile.type, extension };
            updateFile(uploadedFile);
            setUploadProgress(100);
          })
          .catch((error: AxiosError) => {
            if (error.code === 'ERR_CANCELED') {
              return console.error(error.message);
            }
            handleError(error);
          });
      }
    },
    [accepts, uploadLocation, orgId, userId]
  );

  return {
    file,
    errorMessage: getErrorMessage(errorReason),
    uploadProgress,
    maxSize,
    acceptsData,
    onDrop,
    cancelUpload,
    removeFile,
  };
};
